import React, { useEffect, useRef } from 'react';
import { View, StyleSheet, Animated, Platform } from 'react-native';
import { useTheme } from '../../../../theme/useTheme';

// Minimal Column shape needed by the skeleton — must stay in sync with the
// Column type in KfaDataTable.tsx (both live in the same module folder).
export type SkeletonColumn = {
  columnKey: string;
  width?: number;
};

interface SkeletonRowsProps {
  columns: SkeletonColumn[];
  columnWidths: { [key: string]: { minWidth: number; flex: number } };
  rowCount?: number;
}

// ---------------------------------------------------------------------------
// Skeleton loader — renders N shimmer rows matching the column layout.
// A single Animated.Value drives all cells so the pulse stays in sync.
// ---------------------------------------------------------------------------
const SkeletonRows: React.FC<SkeletonRowsProps> = React.memo(
  ({ columns, columnWidths, rowCount = 10 }) => {
    const pulse = useRef(new Animated.Value(0.35)).current;
    const { colors, resolvedTheme } = useTheme();
    const isDark = resolvedTheme === 'dark';

    useEffect(() => {
      const loop = Animated.loop(
        Animated.sequence([
          Animated.timing(pulse, {
            toValue: 0.85,
            duration: 750,
            useNativeDriver: Platform.OS !== 'web',
          }),
          Animated.timing(pulse, {
            toValue: 0.35,
            duration: 750,
            useNativeDriver: Platform.OS !== 'web',
          }),
        ])
      );
      loop.start();
      return () => loop.stop();
    }, [pulse]);

    return (
      <>
        {Array.from({ length: rowCount }).map((_, rowIdx) => (
          <View
            key={rowIdx}
            style={[styles.row, isDark && { borderBottomColor: colors.border.default }]}
          >
            {columns.map((col, colIdx) => {
              const cw = columnWidths[col.columnKey];
              const minWidth = cw ? cw.minWidth : (col.width ?? 120);
              const flex = cw ? cw.flex : 0;
              // Vary bar width per cell so rows don't look identical
              const barPct = 45 + ((rowIdx * 11 + colIdx * 17) % 40);
              return (
                <View key={col.columnKey} style={[styles.cell, { minWidth, flex }]}>
                  <Animated.View
                    style={[
                      styles.bar,
                      { width: `${barPct}%`, opacity: pulse },
                      isDark && { backgroundColor: 'rgba(255,255,255,0.1)' },
                    ]}
                  />
                </View>
              );
            })}
          </View>
        ))}
      </>
    );
  }
);

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    height: 48,
    minHeight: 48,
    borderBottomWidth: 2,
    borderBottomColor: '#D7E5F8',
    width: '100%',
    alignItems: 'center',
  },
  cell: {
    height: 48,
    paddingHorizontal: 16,
    paddingVertical: 14,
    justifyContent: 'center',
    overflow: 'hidden',
  },
  bar: {
    height: 12,
    borderRadius: 6,
    backgroundColor: '#C8D6EA',
  },
});

export default SkeletonRows;
